import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
// import React, { forwardRef } from "react"; used if you want to print Article by clicking button present on ArticleCard component
import SEO, { BreadcrumbComponent } from '../SEO'
import { generateArticleSchema } from '../../services/schemaGenerators'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import PrintRoundedIcon from '@mui/icons-material/PrintRounded'
import MailRoundedIcon from '@mui/icons-material/MailRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import ReportRoundedIcon from '@mui/icons-material/ReportRounded'
import SortByAlphaRoundedIcon from '@mui/icons-material/SortByAlphaRounded'
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import { activityTracker } from '../../services/ActivityTracker'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import BetaVersionNotice from '../BetaVersionNotice'
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Skeleton,
  Fab,
  Zoom,
  useTheme,
  useMediaQuery,
  Tooltip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from '@mui/material'
import { useAuth } from '../../context/AuthContext'
import UpgradePromptModal from '../monetization/UpgradePromptModal'
import LimitedAccessBanner from '../monetization/LimitedAccessBanner'
import useUpgradePromptController from '../monetization/useUpgradePromptController'

// spread operator (...) might creates a shallow copy of the array. When dealing with nested objects, such as the data array within the content array, this can lead to unintentional mutations of the original data structure, for reference: sortDefinitions, to create a deep copy of the article.content we can use a utility function to deep clone objects, such as lodash's cloneDeep method.
import RelatedDocuments from '../RelatedDocuments.js'
import LastUpdatedDisplay from '../LastUpdatedDisplay.js'
import {
  handleEmail,
  handleOpenInNewTab,
} from '../../services/articleCardHandlers'
import { convertLocalDateTimeToISO } from '../../utils/dateUtils'
import {
  parseHtmlSanitizeAddTargetToLinks,
  stripLinksFromHtml,
} from '../../services/utils'
import {
  extractCountryFromSlug,
} from '../../utils/quotaAccessUtils'
import apiService from '../../services/ApiService'
import FeedbackForm from '../FeedbackForm'
import { Enhanced404Handler } from '../Error404'
import { ShareRounded } from '@mui/icons-material'
import { AnnotatedArticle } from '../annotations/EnhancedAnnotatedArticle.js'
import { createSortButtonStyles } from '../../styles/sortButtonDefinitionTableStyles.js'
// Module-level cache
const contentCache = {}

// To enable the PrintRoundedIcon button in the ArticleCard component to print the Article component within it, you need to pass a reference of the Article component to the print function. Utilize a ref to get the Article component's HTML content.
const Article =
  // forwardRef(
  ({
    article,
    searchQuery,
    showSnackbar,
    isInModal = false, // Prop to indicate if component is inside a modal
    parentContainerRef = null, // Reference to parent container for scrolling in modal mode
  }) =>
    // ,ref
    {
      const [articleContent, setArticleContent] = useState(null)
      const [loading, setLoading] = useState(true)

      const [error, setError] = useState(null)
      const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false)
      const [showBackToTop, setShowBackToTop] = useState(false)
      const componentRef = useRef()
      const printContentRef = useRef()
      const fetchedRef = useRef(false) // Track fetch state to prevent duplicate calls
      const [isBookmarked, setIsBookmarked] = useState(false)
      const [bookmarkLoading, setBookmarkLoading] = useState(false)
      // we need to sort lexicographically, all the definitions of Article 1 of CIT law
      const [parsedContent, setParsedContent] = useState(null)
      const [sortedContent, setSortedContent] = useState(null)
      // const [isSorted, setIsSorted] = useState(false);
      const [contentHtml, setContentHtml] = useState(null)
      const [gtlNotesContent, setGtlNotesContent] = useState(null)
      const [gtlNotesTitle, setGtlNotesTitle] = useState('GTL Notes')
      const [isAccordionExpanded, setIsAccordionExpanded] = useState(false)
      const { quotaStatus, refreshQuota, quotaLoading } = useAuth()
      const currentDocumentCountry = useMemo(() => {
        const slug = articleContent?.slug || article?.slug || ''
        return extractCountryFromSlug(slug)
      }, [articleContent?.slug, article?.slug])
      const {
        showUpgradeModal,
        closeUpgradeModal,
        openUpgradeModal,
        upgradeReason,
        linksFilteredInDoc,
        setLinksFilteredInDoc,
        linkAccessExhausted,
        linksFilteredEffective,
        canModifyDocumentFeatures,
        handleQuotaExhausted,
      } = useUpgradePromptController({
        quotaStatus,
        documentCountry: currentDocumentCountry,
        contentLinksFiltered: articleContent?.linksFiltered,
        showSnackbar,
        quotaType: 'link',
      })
      // Theme colors
      const themeColors = useMemo(() => ({
        primary: '#232536',
        secondary: '#ffcf51',
        hover: '#3a3e5a',
      }), [])
      // Create the sort button styles
      const sortButtonStyles = useMemo(
        () => createSortButtonStyles(themeColors),
        [themeColors],
      )
      const theme = useTheme()
      const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))
      const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
      const buttonBorderRadius = '28px'
      // Decide which container to use for scroll detection and scrollToTop action
      const scrollContainer = isInModal ? parentContainerRef : window

      const checkBookmarkStatus = useCallback(async () => {
        if (!articleContent?.slug) return
        try {
          const bookmarkResponse = await apiService.getBookmarkByArticle(
            articleContent.slug,
          )
          if (
            bookmarkResponse &&
            bookmarkResponse.articleSlug === articleContent.slug
          ) {
            setIsBookmarked(true)
            // console.log('Bookmark found:', bookmarkResponse)
          } else {
            setIsBookmarked(false)
          }
        } catch (error) {
          if (error.response?.status === 404) {
            // Article is not bookmarked
            setIsBookmarked(false)
          } else if (error.response?.status === 401) {
            // User not authenticated
            console.warn('User not authenticated for bookmark check')
            setIsBookmarked(false)
          } else {
            // Network or server error
            console.error('Failed to check bookmark status:', error)
            setIsBookmarked(false)
          }
        }
      }, [articleContent?.slug])

      // Extract law info from article data
      const lawInfo = useMemo(() => {
        if (articleContent?.slug) {
          const lawShortName = articleContent.slug.split('-article-')[0]

          const lawNames = {
            'gcc-common-vat-agreement': 'GCC Common VAT Agreement',
            'gcc-common-customs-agreement': 'GCC Common Customs Agreement',
            'gcc-common-excise-agreement': 'GCC Common Excise Agreement',
            'uae-cit-fdl-47-of-2022': 'UAE CIT (FDL No 47 of 2022, as amended)',
            'uae-dmtt-fdl-60-of-2023': 'UAE DMTT (FDL No 60 of 2023)',
            'uae-indirecttax-law-vat-uaevat-8-of-2017-as-amended':
              'UAE VAT (FDL No 8 of 2017, as amended)',
            'uae-tp-fdl-28-of-2022-as-amended':
              'UAE Tax Procedures (FDL No 28 of 2022, as amended)',
            'uae-indirect-excise-7-of-2017-as-amended':
              'UAE Excise Tax (FDL No 7 of 2017, as amended)',
            'uae-fatca-of-2024':
              'UAE FATCA (Foreign Account Tax Compliance Act) 2024',
            'uae-crs-federal-decree-48-of-2018':
              'UAE CRS (Common Reporting Standard, Federal Decree No 48 of 2018)',
            'ksa-income-tax-law-of-2004': 'KSA Income Tax Law 2004',
            'ksa-vat-law-m-113-of-2017': 'KSA VAT Law 2017',
            'ksa-customs-law': 'KSA Customs Law',
            'ksa-fatca-of-2017':
              'KSA FATCA (Foreign Account Tax Compliance Act) 2017',
            'ksa-excise-law-of-2019': 'KSA Excise Law 2019',
            'ksa-zakat-law-of-1951': 'KSA Zakat Collection Law 1951',
            'kuwait-draft-business-profit-tax-law-of-2024':
              'Kuwait Draft Business Profit Tax Law 2024',
            'kuwait-kit-law-no-2-of-2008-amending-decree-no-3-of-1955':
              'Kuwait Income Tax Law - Decree No. 3 of 1955',
            'kuwait-qdmtt-decree-law-157-of-2024':
              'Kuwait QDMTT Law - Decree No. 157 of 2024',
            'qatar-income-tax-law-24-of-2018':
              'Qatar Income Tax Law 24 of 2018',
            'qatar-dmtt-fdl-22-of-2024': 'Qatar DMTT (FDL No 22 of 2024)',
            'qatar-tax-procedures-law': 'Qatar Tax Procedures Law',
            'qatar-excise-law-25-of-2018': 'Qatar Excise Law 2018',
            'qatar-fatca-of-2014-iga-m1':
              'Qatar FATCA (Foreign Account Tax Compliance Act) 2014',
            'oman-income-tax-law-royal-decree-28-of-2009':
              'Oman Income Tax Law (Royal Decree No 28 of 2009, as amended)',
            'oman-vat-law-royal-decree-121-of-2020':
              'Oman VAT Law - Royal Decree No. 121 of 2020',
            'oman-excise-tax-law-royal-decree-23-of-2019':
              'Oman Excise Tax Law (Royal Decree No 23 of 2019)',
            'oman-fatca-of-2014-direct':
              'Oman FATCA (Foreign Account Tax Compliance Act) 2014',
            'oman-cbcr-law': 'Oman Country-by-Country Reporting Law',
            'bahrain-dmtt-law-11-of-2024':
              'Bahrain DMTT Law (Decree Law No. 11 of 2024)',
            'bahrain-vat-law-48-of-2018': 'Bahrain VAT Law 48 of 2018',
          }
          return {
            lawShortName,
            lawFullName: lawNames[lawShortName] || 'Tax Law',
          }
        }
        return null
      }, [articleContent])

      useEffect(() => {
        if (articleContent?.slug && !quotaStatus && !quotaLoading) {
          // console.log('🔵 Refreshing quota for article:', articleContent.slug)
          refreshQuota()
        }
      }, [articleContent, quotaStatus, quotaLoading, refreshQuota])

      useEffect(() => {
        if (articleContent?.slug) {
          checkBookmarkStatus()
          // Track article view if not in modal
          if (!isInModal) {
            activityTracker.trackDocumentView(
              'article',
              articleContent.slug,
              articleContent.title?.replace(/<[^>]*>/g, ''), // Remove HTML tags
              {
                number: articleContent.number,
                pathString: articleContent.pathString,
                // lawId: articleContent.slug.split('-article-')[0], // Extract law from slug
              },
            )
          }
        }
      }, [articleContent, isInModal, checkBookmarkStatus])
      // Show back to top button when scrolled down
      useEffect(() => {
        if (!scrollContainer) return
        const modalScrollContainer =
          isInModal && scrollContainer?.current
            ? scrollContainer.current
            : null

        const handleScroll = () => {
          // For window, use scrollY; for element, use scrollTop
          const scrollPosition = isInModal
            ? modalScrollContainer?.scrollTop
            : window.scrollY

          if (scrollPosition > 300) {
            setShowBackToTop(true)
          } else {
            setShowBackToTop(false)
          }
        }

        // Add scroll listener to the appropriate container
        if (modalScrollContainer) {
          modalScrollContainer.addEventListener('scroll', handleScroll)
        } else {
          window.addEventListener('scroll', handleScroll)
        }

        return () => {
          // Clean up scroll listener
          if (modalScrollContainer) {
            modalScrollContainer.removeEventListener('scroll', handleScroll)
          } else {
            window.removeEventListener('scroll', handleScroll)
          }
        }
      }, [isInModal, scrollContainer])

      const handleBookmark = async () => {
        if (bookmarkLoading) return
        if (!isBookmarked && !canModifyDocumentFeatures) {
          showSnackbar?.(
            'This document is in view-only mode. Upgrade or wait for quota reset to add bookmarks.',
            'warning',
          )
          return
        }

        setBookmarkLoading(true)
        try {
          if (isBookmarked) {
            // Remove bookmark
            await apiService.deleteBookmarkByArticle(articleContent.slug)
            setIsBookmarked(false)

            // Track bookmark removal
            activityTracker.trackDocumentAction(
              'unbookmark',
              'article',
              articleContent.slug,
              articleContent.title?.replace(/<[^>]*>/g, ''),
            )

            showSnackbar?.('Bookmark removed', 'info')
          } else {
            // Add bookmark
            const bookmarkData = {
              articleSlug: articleContent.slug,
              title:
                articleContent.title?.replace(/<[^>]*>/g, '') ||
                'Untitled Article',
              url: `/articles/${articleContent.slug}`,
              category: 'Article',
              description:
                articleContent.summary ||
                (articleContent.textOnly
                  ? articleContent.textOnly.substring(0, 200) + '...'
                  : ''),
              tags: articleContent.tags || [],
            }

            await apiService.createBookmark(bookmarkData)
            setIsBookmarked(true)

            // Track bookmark addition
            activityTracker.trackDocumentAction(
              'bookmark',
              'article',
              articleContent.slug,
              articleContent.title?.replace(/<[^>]*>/g, ''),
            )

            showSnackbar?.('Article bookmarked!', 'success')
          }
        } catch (error) {
          console.error('Bookmark error:', error)
          const errorMessage =
            error.response?.data?.message || 'Failed to update bookmark'
          showSnackbar?.(errorMessage, 'error')
        } finally {
          setBookmarkLoading(false)
        }
      }
      // Scroll to top function
      const scrollToTop = useCallback(() => {
        if (isInModal && parentContainerRef?.current) {
          parentContainerRef.current.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
        } else {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
        }
      }, [isInModal, parentContainerRef])
      const extractGtlNotes = useCallback((htmlContent) => {
        try {
          if (!htmlContent) return null

          const parser = new DOMParser()
          const doc = parser.parseFromString(htmlContent, 'text/html')

          // Debug: log all h3.linktogtl elements
          const allGtlHeadings = doc.querySelectorAll('h3.linktogtl')
          // Look for the GTL Notes heading (not the "See GTL Notes" one)
          let gtlHeading = null

          for (const heading of allGtlHeadings) {
            const anchorTag = heading.querySelector('a')
            if (anchorTag) {
              const text = anchorTag.textContent.trim()
              const id = anchorTag.id

              // Look for the actual GTL Notes heading (usually has id="bookmark" and text starts with "GTL Notes")
              if (text.startsWith('GTL Notes') && id === 'bookmark') {
                gtlHeading = heading
                break
              }
              // Alternative: look for heading that doesn't contain brackets (not "[See GTL Notes]")
              else if (
                text.includes('GTL Notes') &&
                !text.includes('[') &&
                !text.includes(']')
              ) {
                gtlHeading = heading
                break
              }
            } else {
              // Links may be stripped when quota is exhausted; fall back to heading text.
              const headingText = heading.textContent.trim()
              if (
                headingText.startsWith('GTL Notes') &&
                !headingText.startsWith('See')
              ) {
                gtlHeading = heading
                break
              }
            }
          }

          if (!gtlHeading) {
            // console.log('No GTL Notes heading found')
            return null
          }

          // Get the title from the anchor tag or heading text (if links stripped)
          const anchorTag = gtlHeading.querySelector('a')
          const title = anchorTag
            ? anchorTag.textContent.trim()
            : gtlHeading.textContent.trim() || 'GTL Notes'

          // Find all content after the GTL Notes heading
          let currentElement = gtlHeading.nextElementSibling
          const gtlContent = []

          // Get all following sibling elements until we hit another h3 or end of container
          while (currentElement) {
            if (currentElement.tagName === 'H3') {
              break
            }
            gtlContent.push(currentElement.outerHTML)
            currentElement = currentElement.nextElementSibling
          }
          return gtlContent.length > 0
            ? {
                title,
                content: gtlContent.join(''),
              }
            : null
        } catch (error) {
          console.warn('Error extracting GTL Notes:', error)
          return null
        }
      }, [])

      const removeGtlNotesFromContent = useCallback((htmlContent) => {
        if (!htmlContent) return htmlContent

        const parser = new DOMParser()
        const doc = parser.parseFromString(htmlContent, 'text/html')

        // Find the GTL Notes heading (same logic as extraction)
        const allGtlHeadings = doc.querySelectorAll('h3.linktogtl')
        let gtlHeading = null

        for (const heading of allGtlHeadings) {
          const anchorTag = heading.querySelector('a')
          if (anchorTag) {
            const text = anchorTag.textContent.trim()
            const id = anchorTag.id

            // Look for the actual GTL Notes heading
            if (text.startsWith('GTL Notes') && id === 'bookmark') {
              gtlHeading = heading
              break
            } else if (
              text.includes('GTL Notes') &&
              !text.includes('[') &&
              !text.includes(']')
            ) {
              gtlHeading = heading
              break
            }
          } else {
            const headingText = heading.textContent.trim()
            if (
              headingText.startsWith('GTL Notes') &&
              !headingText.startsWith('See')
            ) {
              gtlHeading = heading
              break
            }
          }
        }

        if (gtlHeading) {
          const elementsToRemove = [gtlHeading]

          // Remove all elements following the GTL Notes heading until next h3 or end
          let currentElement = gtlHeading.nextElementSibling
          while (currentElement) {
            if (currentElement.tagName === 'H3') {
              break
            }
            elementsToRemove.push(currentElement)
            currentElement = currentElement.nextElementSibling
          }

          // Remove all collected elements
          elementsToRemove.forEach((el) => el.remove())
        }

        return doc.body.innerHTML
      }, [])

      const removeAllLinksFromContent = useCallback((htmlContent) => {
        return stripLinksFromHtml(htmlContent || '')
      }, [])

      const renderQuotaFilteredContent = useCallback(
        (rawHtml) => {
          // const contentWithoutGtl = removeGtlNotesFromContent(rawHtml || '')
          return removeAllLinksFromContent(rawHtml || '')
        },
        [removeAllLinksFromContent],
      )

      const memoizedGtlNotes = useMemo(() => {
        return articleContent?.content
          ? extractGtlNotes(articleContent.content)
          : null
      }, [articleContent?.content, extractGtlNotes])
      // * Here we are creating dynamic link tag for this particular component
      useEffect(() => {
        const environment = process.env.REACT_APP_ENVIRONMENT || 'dev'
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = `https://gtlcdn-eufeh8ffbvbvacgf.z03.azurefd.net/guide/stylesheets/${environment}/article.css`
        link.id = 'external-css'

        if (!document.getElementById('external-css')) {
          document.head.appendChild(link)
        }

        return () => {
          document.getElementById('external-css')?.remove() // Cleanup on unmount
        }
      }, [])
      // SEO Data Generation
      const generateSEOData = useCallback(() => {
        if (!articleContent) return null

        // Extract law information from slug
        const countryCode = articleContent.slug?.split('-')[0] || 'uae'
        const country =
          countryCode === 'uae' ? 'UAE' : countryCode.toUpperCase()

        // Generate title based on article data
        const seoTitle =
          articleContent.metaTitle ||
          (articleContent.title
            ? `${articleContent.title.replace(
                /<[^>]*>/g,
                '',
              )} | ${country} Tax Law | GCC Tax Laws`
            : `Article ${
                articleContent.number || ''
              } | ${country} Tax Law | GCC Tax Laws`)

        // Generate description from content or use default
        const seoDescription =
          articleContent.metaDescription ||
          articleContent.summary ||
          (articleContent.textOnly
            ? articleContent.textOnly.substring(0, 155) + '...'
            : `Detailed analysis of ${
                articleContent.title || 'tax law article'
              } under ${country} tax legislation.`)

        // Generate canonical URL
        const canonicalUrl = `https://gcctaxlaws.com/articles/${articleContent.slug}`

        // Generate keywords based on content
        const keywords =
          articleContent.metaKeywords ||
          [
            `${country} tax law`,
            articleContent?.content?.toLowerCase().includes('definition')
              ? 'tax definitions'
              : null,
            articleContent.slug?.includes('cit')
              ? 'corporate income tax'
              : null,
            articleContent.slug?.includes('vat') ? 'value added tax' : null,
            'GCC tax laws',
            'tax compliance',
            `article ${articleContent.number}`,
          ]
            .filter(Boolean)
            .join(', ')

        // Generate schema markup
        const schemaMarkup = generateArticleSchema(articleContent)
        const modifiedDate = convertLocalDateTimeToISO(articleContent.updatedAt)
        const publishedDate = convertLocalDateTimeToISO(
          articleContent.updatedAt,
        )
        return {
          title: seoTitle,
          description: seoDescription,
          canonical: canonicalUrl,
          keywords,
          schemaMarkup,
          publishedDate,
          modifiedDate,
          country,
          lawName: articleContent?.lawId,
        }
      }, [articleContent])

      // Fetch article content if needed
      useEffect(() => {
        const getArticleContent = async () => {
          const shouldRefetchInlineFilteredContent =
            Boolean(article?.content) &&
            Boolean(article?.linksFiltered) &&
            Boolean(article?.slug) &&
            canModifyDocumentFeatures

          // If we already have full article data, no need to fetch again
          if (article?.content && !shouldRefetchInlineFilteredContent) {
            setArticleContent(article)
            setLinksFilteredInDoc(Boolean(article?.linksFiltered))
            if (!article?.linksFiltered && !linksFilteredInDoc && !linkAccessExhausted) {
              closeUpgradeModal()
            }
            setLoading(false)
            return
          }

          // Skip if no slug or already fetching
          if (!article?.slug || fetchedRef.current) {
            return
          }

          // Check module-level cache first
          const cachedContent = contentCache[article.slug]
          const shouldBypassFilteredCache =
            shouldRefetchInlineFilteredContent ||
            (Boolean(cachedContent?.linksFiltered) && canModifyDocumentFeatures)

          if (cachedContent && !shouldBypassFilteredCache) {
            setArticleContent(cachedContent)
            setLinksFilteredInDoc(Boolean(cachedContent?.linksFiltered))
            if (!cachedContent?.linksFiltered && !linksFilteredInDoc && !linkAccessExhausted) {
              closeUpgradeModal()
            }
            setLoading(false)
            return
          }

          if (shouldBypassFilteredCache) {
            delete contentCache[article.slug]
          }
          setLoading(true)
          fetchedRef.current = true

          try {
            const requestOptions = shouldBypassFilteredCache
              ? { bypassCache: true, skipCache: true }
              : {}

            // Use the service function
            // const data = await fetchArticleBySlug(article.slug)
            // const data = await apiService.getArticleBySlug(article.slug)
            const data = await apiService.getWithQuotaRefresh(
              `/api/v1/articles/slug/${article.slug}`,
              {},
              requestOptions,
            )
            // Check monetization metadata
            if (data.linksFiltered) {
              console.warn('Links filtered in article:', article.slug)
              setLinksFilteredInDoc(true)
              // Show upgrade prompt to user
              showSnackbar?.(
                data.upgradeMessage ||
                  'Some features are limited. Upgrade for full access.',
                'warning',
              )
            }
            setLinksFilteredInDoc(Boolean(data.linksFiltered))
            if (!data.linksFiltered && !linksFilteredInDoc && !linkAccessExhausted) {
              closeUpgradeModal()
            }

            // Log access source for analytics
            // console.log('Article accessed via:', data.accessSource)
            // Store in module-level cache
            contentCache[article.slug] = data
            setArticleContent(data)
            setError(null)
            // Refresh quota status after successful access
            setTimeout(() => refreshQuota(), 500) // non‑blocking, updates quota “later”, allows UI to proceed without waiting
            // await refreshQuota() // waits for the call to finish, guarantees banner updates before continuing, can cause brief loading/UX delay
          } catch (err) {
            console.error('Error fetching article content:', err.message)
            // Handle 402 specifically (shouldn't happen for articles, but be safe)
            if (err.response?.status === 402) {
              const reason =
                err.isQuotaError && err.quotaData?.reason
                  ? err.quotaData.reason
                  : 'no_credits'
              setError(
                'Payment Required: ' +
                  (err.response.data?.message ||
                    err.userMessage ||
                    'Please upgrade to access this content'),
              )
              openUpgradeModal(reason)
              showSnackbar?.(
                'Please purchase a plan to access this content',
                'error',
              )
            } else {
              console.error('Error fetching article content:', err.message)
              setError(err.message)
            }
            fetchedRef.current = false // Allow retry on error
          } finally {
            setLoading(false)
          }
        }

        getArticleContent()

        // Cleanup function
        return () => {
          fetchedRef.current = false
        }
      }, [
        article,
        canModifyDocumentFeatures,
        closeUpgradeModal,
        linkAccessExhausted,
        linksFilteredInDoc,
        openUpgradeModal,
        refreshQuota,
        setLinksFilteredInDoc,
        showSnackbar,
      ])

      // Generate SEO data
      const seoData = generateSEOData()

      // Parse HTML content when article content changes, so we can check whether it contains any tables or not, if yes then we can show sort button
      useEffect(() => {
        if (articleContent?.content) {
          // Parse the HTML content
          const parser = new DOMParser()
          const doc = parser.parseFromString(
            articleContent.content,
            'text/html',
          )
          setParsedContent(doc)
          setSortedContent(null) // Reset sorted content when article changes
          const shouldFilterLinks = linksFilteredEffective
          // Extract GTL Notes
          if (memoizedGtlNotes) {
            setGtlNotesTitle(memoizedGtlNotes.title)
            setGtlNotesContent(
              shouldFilterLinks
                ? renderQuotaFilteredContent(memoizedGtlNotes.content)
                : memoizedGtlNotes.content,
            )

            // Remove GTL Notes from main content
            const contentWithoutGtl = removeGtlNotesFromContent(
              articleContent.content,
            )
            setContentHtml(
              shouldFilterLinks
                ? renderQuotaFilteredContent(contentWithoutGtl)
                : contentWithoutGtl,
            )
          } else {
            setGtlNotesContent(null)
            setContentHtml(
              shouldFilterLinks
                ? renderQuotaFilteredContent(articleContent.content)
                : articleContent.content,
            )
          }
        }
      }, [
        articleContent?.content,
        articleContent?.linksFiltered,
        linksFilteredInDoc,
        linksFilteredEffective,
        memoizedGtlNotes,
        removeGtlNotesFromContent,
        renderQuotaFilteredContent,
      ])

      // below function is used to sort all the tables, we just need to sort the table where definitions are there in the article content
      const sortDefinitions = useCallback(() => {
        if (!parsedContent) return

        // Create a deep clone of the parsed content (without GTL Notes)
        const baseContent = memoizedGtlNotes
          ? removeGtlNotesFromContent(articleContent?.content || '')
          : articleContent?.content || ''

        const parser = new DOMParser()
        const doc = parser.parseFromString(baseContent, 'text/html')
        const docClone = doc.cloneNode(true)

        // Find all tables in the document
        // const tables = docClone.querySelectorAll('table')

        // Find only tables with class="definition"
        const tables = docClone.querySelectorAll('table.definition')

        tables.forEach((table) => {
          // Get all rows except header row (if any)
          // const rows = Array.from(table.querySelectorAll('tr')).slice(1)

          // Get all rows for sorting including header row
          const rows = Array.from(table.querySelectorAll('tr'))

          if (rows.length > 0) {
            // Sort rows by the text in the first column
            rows.sort((a, b) => {
              const aText = a.querySelector('td:first-child')?.textContent || ''
              const bText = b.querySelector('td:first-child')?.textContent || ''
              return aText.localeCompare(bText)
            })

            // Remove existing rows
            rows.forEach((row) => row.parentNode.removeChild(row))

            // Append sorted rows
            const tbody = table.querySelector('tbody') || table
            rows.forEach((row) => tbody.appendChild(row))
          }
        })

        setSortedContent(docClone)
        setContentHtml(docClone.body.innerHTML)

        if (showSnackbar) {
          showSnackbar('Definitions sorted alphabetically', 'success')
        }
      }, [
        parsedContent,
        showSnackbar,
        memoizedGtlNotes,
        articleContent,
        removeGtlNotesFromContent,
      ])

      // Reset to original content
      const resetToDefault = useCallback(() => {
        setSortedContent(null)

        // Reset to content without GTL Notes
        const baseContent = memoizedGtlNotes
          ? removeGtlNotesFromContent(articleContent?.content || '')
          : articleContent?.content || ''

        setContentHtml(baseContent)

        // Provide feedback when resetting
        if (showSnackbar) {
          showSnackbar('Restored original order', 'info')
        }
      }, [
        articleContent,
        showSnackbar,
        memoizedGtlNotes,
        removeGtlNotesFromContent,
      ])

      const handleFeedbackFormToggle = useCallback(() => {
        setIsFeedbackFormOpen((prev) => !prev)
      }, [])
      const handleShare = useCallback(async () => {
        const url = `${window.location.origin}/articles/${
          articleContent?.slug || article?.slug
        }`
        const title = articleContent?.title || article?.title || 'Article'

        // Remove HTML tags from title for clean sharing
        const cleanTitle = title.replace(/<[^>]*>/g, '')

        const shareData = {
          title: cleanTitle,
          text: `Check out this article: ${cleanTitle}`,
          url: url,
        }

        try {
          let shareMethod = 'shared via link' // default
          // Check if Web Share API is supported (mainly mobile)
          if (navigator.share) {
            shareMethod = 'shared via native api'
            await navigator.share(shareData)
            if (showSnackbar) {
              showSnackbar('Article shared successfully! 📤', 'success')
            }
          } else {
            // Fallback to clipboard copy
            await navigator.clipboard.writeText(url)
            if (showSnackbar) {
              showSnackbar('Article link copied to clipboard! 📋', 'success')
            }
          }
          activityTracker.trackDocumentAction(
            shareMethod,
            'article',
            articleContent?.slug || article?.slug,
            cleanTitle,
          )
        } catch (error) {
          if (error.name !== 'AbortError') {
            // Fallback to clipboard if share fails
            try {
              await navigator.clipboard.writeText(url)
              if (showSnackbar) {
                showSnackbar('Article link copied to clipboard! 📋', 'success')
              }
              activityTracker.trackDocumentAction(
                'shared via link',
                'article',
                articleContent?.slug || article?.slug,
                cleanTitle,
              )
            } catch (clipboardError) {
              if (showSnackbar) {
                showSnackbar('Failed to share article', 'error')
              }
            }
          }
        }
      }, [articleContent, article, showSnackbar])

      const handlePrint = useCallback(() => {
        if (componentRef.current) {
          const wasAccordionExpanded = isAccordionExpanded

          // If GTL Notes exist and accordion is closed, expand it

          if (gtlNotesContent && !isAccordionExpanded) {
            setIsAccordionExpanded(true)

            // Wait for accordion to expand before printing

            setTimeout(() => {
              performPrint()

              // Restore original accordion state after printing

              setIsAccordionExpanded(wasAccordionExpanded)
            }, 300) // Give accordion time to expand
          } else {
            // No GTL Notes or accordion already expanded, print immediately

            performPrint()
          }
        }

        function performPrint() {
          const printWindow = window.open('', '_blank')
          const sourceNode = printContentRef.current || componentRef.current
          if (!printWindow || !sourceNode) return
          const printDoc = printWindow.document
          printDoc.title = 'GCC Tax Laws'
          printDoc.body.replaceChildren()

          // document.styleSheets enumerates every active sheet; .href is already
          // absolute and .cssRules captures emotion’s speedy/insertRule styles in prod.
          const styleLoadPromises = []
          const seenHrefs = new Set()
          Array.from(document.styleSheets).forEach((sheet) => {
            try {
              if (sheet.disabled) return
              if (sheet.href) {
                if (seenHrefs.has(sheet.href)) return
                seenHrefs.add(sheet.href)
                const link = printDoc.createElement('link')
                link.rel = 'stylesheet'
                link.type = 'text/css'
                link.href = sheet.href
                const loaded = new Promise((resolve) => {
                  link.onload = resolve
                  link.onerror = resolve
                  setTimeout(resolve, 1500)
                })
                styleLoadPromises.push(loaded)
                printDoc.head.appendChild(link)
              } else {
                const styleEl = printDoc.createElement('style')
                if (sheet.cssRules && sheet.cssRules.length > 0) {
                  styleEl.textContent = Array.from(sheet.cssRules)
                    .map((rule) => rule.cssText)
                    .join('\n')
                } else if (sheet.ownerNode?.textContent) {
                  styleEl.textContent = sheet.ownerNode.textContent
                }
                printDoc.head.appendChild(styleEl)
              }
            } catch (_e) {
              // CORS-blocked sheets are inaccessible via cssRules but are
              // already linked via their href branch above skip silently.
            }
          })

          const printStyle = printDoc.createElement('style')
          printStyle.textContent = `
            body { margin: 0; padding: 16px; }
            .article-toolbar,
            .article-toolbar-wrapper { display: none !important; }
          `
          printDoc.head.appendChild(printStyle)

          // Clone and strip toolbar UI so page 1 starts with article content
          const clone = sourceNode.cloneNode(true)
          const toolbarEl = clone.querySelector('.article-toolbar')
          if (toolbarEl) toolbarEl.remove()
          const toolbarWrapper = clone.querySelector('.article-toolbar-wrapper')
          if (toolbarWrapper) toolbarWrapper.remove()

          // Replace all anchor tags with plain text so the PDF has no clickable links
          clone.querySelectorAll('a').forEach((link) => {
            link.replaceWith(document.createTextNode(link.textContent || ''))
          })

          printDoc.body.appendChild(clone)

          const triggerPrint = () => {
            let isClosed = false
            const closePrintWindow = () => {
              if (isClosed || printWindow.closed) return
              isClosed = true
              printWindow.close()
            }

            // Register before print() so we don't miss the event.
            printWindow.onafterprint = closePrintWindow

            // Fallback for browsers where onafterprint is inconsistent on cancel.
            let hasBlurredForPrintDialog = false
            const onBlur = () => {
              hasBlurredForPrintDialog = true
            }
            const onFocus = () => {
              if (hasBlurredForPrintDialog) {
                setTimeout(closePrintWindow, 150)
              }
            }
            printWindow.addEventListener('blur', onBlur, { once: true })
            printWindow.addEventListener('focus', onFocus, { once: true })

            printWindow.focus()
            printWindow.print()
            activityTracker.trackDocumentAction(
              'print',
              'article',
              articleContent?.slug || article?.slug,
              (articleContent?.title || article?.title)?.replace(
                /<[^>]*>/g,
                '',
              ),
            )
          }

          Promise.all(styleLoadPromises).finally(() => {
            setTimeout(() => {
              if (printWindow.requestAnimationFrame) {
                printWindow.requestAnimationFrame(triggerPrint)
              } else {
                triggerPrint()
              }
            }, 100)
          })
        }
      }, [gtlNotesContent, isAccordionExpanded, articleContent, article])

      useEffect(() => {
        const handlePrintShortcut = (event) => {
          const isPrintShortcut =
            (event.ctrlKey || event.metaKey) && event.key?.toLowerCase() === 'p'
          if (!isPrintShortcut) return

          event.preventDefault()
          event.stopPropagation()
          handlePrint()
        }

        window.addEventListener('keydown', handlePrintShortcut)
        return () => {
          window.removeEventListener('keydown', handlePrintShortcut)
        }
      }, [handlePrint])
      // With this updated check that only looks for tables with class="definition":
      const hasDefinitionTables =
        parsedContent?.querySelectorAll('table.definition').length > 0

      // useEffect will work if you apply proper querySelector, for example you have written find all anchor tags who have target="_blank", but if you try to inspect the article in browser, you won't find the target attribute in anchor tag, maybe becuase of DOMPurify.sanitize()
      // Method 2 Trying html-react-parser -  avoiding potential memory leaks from improper handling of event listeners

      if (loading || quotaLoading) {
        return (
          <Box sx={{ padding: 2, margin: 2 }}>
            <Skeleton variant='rectangular' width='100%' height={200} />
            <Skeleton variant='text' width='60%' />
            <Skeleton variant='text' width='80%' />
          </Box>
        )
      }
      if (error) {
        return (
          <Enhanced404Handler
            error={error}
            article={article}
            showFullDetails={process.env.REACT_APP_ENVIRONMENT === 'dev'} // Show details only in dev
          />
        )
      }
      return (
        <>
          {/* SEO Component - Only add if not in modal and we have content */}
          {!isInModal && seoData && (
            <SEO
              title={seoData.title}
              description={seoData.description}
              canonical={seoData.canonical}
              keywords={seoData.keywords}
              schemaMarkup={seoData.schemaMarkup}
              publishedDate={seoData.publishedDate}
              modifiedDate={seoData.modifiedDate}
              country={seoData.country}
              lawName={seoData.lawName}
              articleType='article'
            />
          )}
          {!isInModal && articleContent && (
            <BreadcrumbComponent
              currentItem={articleContent}
              itemType='article'
              lawInfo={lawInfo}
              country={articleContent.slug.split('-')[0]}
            />
          )}
          {articleContent?.betaVersion && (
            <BetaVersionNotice
              show={articleContent.betaVersion}
              isInModal={isInModal}
            />
          )}
          {/* Upgrade prompt banner if links filtered */}
          {linksFilteredEffective && (
            <LimitedAccessBanner
              onUpgrade={() => openUpgradeModal('link_filtered')}
              sx={{
                mt: isInModal ? 6 : 2,
                clear: isInModal ? 'both' : 'none',
              }}
            />
          )}
          <Paper
            ref={componentRef}
            // ref={ref}
            sx={{ padding: 2, margin: 2, borderRadius: '8px' }}
          >
            <Box
              display='flex'
              justifyContent='flex-end'
              alignItems='center'
              className='article-toolbar-wrapper'
            >
              <Box className='article-toolbar'>
                {hasDefinitionTables && (
                  <Tooltip
                    title={
                      sortedContent
                        ? 'Restore the order of definitions back'
                        : 'Sort Definitions Alphabetically'
                    }
                    arrow
                  >
                    <IconButton
                      aria-label={
                        sortedContent
                          ? 'Restore the order of definitions back'
                          : 'Sort Definitions Alphabetically'
                      }
                      onClick={(e) => {
                        e.stopPropagation()
                        sortedContent ? resetToDefault() : sortDefinitions()
                      }}
                      size={isMobileScreen ? 'small' : 'medium'}
                      sx={sortedContent ? {} : sortButtonStyles}
                    >
                      {sortedContent ? (
                        <RestoreRoundedIcon
                          sx={{ color: themeColors.primary }}
                        />
                      ) : (
                        <SortByAlphaRoundedIcon
                          sx={{ color: themeColors.primary, fontSize: 28 }}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip
                  title={
                    isFeedbackFormOpen
                      ? 'Close feedback form'
                      : 'Report Broken Links in this Article'
                  }
                  arrow
                >
                  <IconButton
                    aria-label={
                      isFeedbackFormOpen
                        ? 'Close feedback form'
                        : 'Report Broken Links in this Article'
                    }
                    onClick={(e) => {
                      e.stopPropagation()
                      handleFeedbackFormToggle()
                    }}
                    size={isMobileScreen ? 'small' : 'medium'}
                  >
                    {isFeedbackFormOpen ? (
                      <CloseRoundedIcon color='error' />
                    ) : (
                      <ReportRoundedIcon color='error' />
                    )}
                  </IconButton>
                </Tooltip>
                {process.env.REACT_APP_ENVIRONMENT !== 'prod' && (
                  <Tooltip title='Print this article' arrow>
                    <IconButton
                      aria-label='Print this article'
                      onClick={(e) => {
                        e.stopPropagation()
                        handlePrint()
                      }}
                      size={isMobileScreen ? 'small' : 'medium'}
                    >
                      <PrintRoundedIcon sx={{ color: themeColors.primary }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title='Share this article' arrow>
                  <IconButton
                    aria-label='Share this article'
                    onClick={(e) => {
                      e.stopPropagation()
                      handleShare()
                    }}
                    size={isMobileScreen ? 'small' : 'medium'}
                  >
                    <ShareRounded sx={{ color: themeColors.primary }} />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    !canModifyDocumentFeatures && !isBookmarked
                      ? 'View-only mode: no active subscription. Upgrade to enable bookmarking.'
                      : isBookmarked
                        ? 'Remove bookmark'
                        : 'Bookmark this article'
                  }
                  arrow
                >
                  <IconButton
                    aria-label={
                      isBookmarked ? 'Remove bookmark' : 'Bookmark this article'
                    }
                    onClick={(e) => {
                      e.stopPropagation()
                      handleBookmark()
                    }}
                    disabled={bookmarkLoading}
                    size={isMobileScreen ? 'small' : 'medium'}
                    sx={{ position: 'relative' }}
                  >
                    {bookmarkLoading ? (
                      <CircularProgress
                        size={isMobileScreen ? 16 : 20}
                        sx={{ color: themeColors.primary }}
                      />
                    ) : isBookmarked ? (
                      <BookmarkIcon
                        sx={{
                          color: themeColors.secondary,
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                        }}
                      />
                    ) : (
                      <BookmarkBorderIcon sx={{ color: themeColors.primary }} />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title='Mail this article' arrow>
                  <IconButton
                    aria-label='Mail this article'
                    // title='Mail this article'
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEmail(articleContent || article)
                    }}
                    size={isMobileScreen ? 'small' : 'medium'}
                  >
                    <MailRoundedIcon
                      sx={{ color: themeColors.primary }}
                      // color='primary'
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Open this article in new tab' arrow>
                  <IconButton
                    aria-label='Open this article in new tab'
                    // title='Open this article in new tab'
                    onClick={(e) => {
                      e.stopPropagation()
                      handleOpenInNewTab(
                        articleContent || article,
                        `/articles/${article?.slug}`,
                        showSnackbar,
                      )
                    }}
                    size={isMobileScreen ? 'small' : 'medium'}
                  >
                    {/*fontSize: (default 24px), OR large, (default medium), small, string */}
                    <OpenInNewRoundedIcon
                      // sx={{ fontSize: 32 }}
                      sx={{ color: themeColors.primary }}
                      // color='primary'
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <LastUpdatedDisplay
              updatedAt={articleContent?.updatedAt}
              isInModal={isInModal}
            />
            {isFeedbackFormOpen && (
              <FeedbackForm
                articleId={articleContent?.slug || article?.slug}
                onClose={handleFeedbackFormToggle}
                showSnackbar={showSnackbar} // Pass showSnackbar to FeedbackForm
              />
            )}
            {/* Display the parsed HTML content */}
            {contentHtml ? (
              <AnnotatedArticle
                documentId={articleContent?.slug || article?.slug || 'unknown'}
                documentType='article'
                enableAnnotations={true}
                allowAnnotationEdits={canModifyDocumentFeatures}
                readOnlyMessage='This document is in view-only mode. Upgrade or wait for quota reset to add or edit annotations.'
              >
                <Box mt={2} ref={printContentRef}>
                  {parseHtmlSanitizeAddTargetToLinks(
                    contentHtml,
                    searchQuery,
                    '/articles/' + (articleContent?.slug || article?.slug),
                    {
                      fullDocumentContent:
                        articleContent?.content || contentHtml,
                      documentTitle:
                        articleContent?.title || article?.title || 'Article',
                      showSnackbar: showSnackbar,
                      quotaStatus: quotaStatus,
                      enforceLinkQuota: true,
                      linkAccessExhausted: linkAccessExhausted,
                      onQuotaExhausted: handleQuotaExhausted,
                      refreshQuota: refreshQuota,
                    },
                  )}
                  {/* GTL Notes Accordion */}
                  {gtlNotesContent && (
                    <Box mt={3}>
                      <Accordion
                        expanded={isAccordionExpanded}
                        onChange={(event, expanded) =>
                          setIsAccordionExpanded(expanded)
                        }
                        aria-label='GTL Notes section'
                        className='gtl-notes-accordion'
                        sx={{
                          '&:before': {
                            display: 'none',
                          },
                          boxShadow: 'none',
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: '8px !important',
                          '&:not(:last-child)': {
                            borderBottom: 0,
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          className='gtl-notes-accordion-summary'
                          sx={{
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.05)'
                                : 'rgba(0, 0, 0, 0.03)',
                            borderRadius: '8px',
                            '&.Mui-expanded': {
                              borderBottomLeftRadius: 0,
                              borderBottomRightRadius: 0,
                            },
                          }}
                        >
                          <Typography
                            variant='h6'
                            sx={{
                              fontWeight: 600,
                              color: '#43af76', // Use your custom green color instead of themeColors.primary
                            }}
                          >
                            {gtlNotesTitle}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{ padding: 2 }}
                          className='gtl-notes-accordion-details'
                        >
                          <Box className='gtl-notes-content'>
                            {parseHtmlSanitizeAddTargetToLinks(
                              gtlNotesContent,
                              searchQuery,
                              '/articles/' +
                                (articleContent?.slug || article?.slug),
                              {
                                fullDocumentContent:
                                  articleContent?.content || gtlNotesContent,
                                documentTitle: gtlNotesTitle,
                                showSnackbar: showSnackbar,
                                enforceLinkQuota: true,
                                linkAccessExhausted: linkAccessExhausted,
                                onQuotaExhausted: handleQuotaExhausted,
                                refreshQuota: refreshQuota,
                              },
                            )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  )}
                  {!linksFilteredEffective &&
                    articleContent?.relatedDocs &&
                    articleContent.relatedDocs.length > 0 && (
                      <RelatedDocuments
                        relatedDocs={articleContent.relatedDocs}
                        showSnackbar={showSnackbar}
                      />
                    )}
                </Box>
              </AnnotatedArticle>
            ) : (
              <Typography>
                We're currently building the most comprehensive tax law
                repository for the GCC region. If you happen to have this
                particular document, we'd be truly grateful if you could share
                it with us.
                <br />
                Your contribution will not only enhance our database but also
                help the entire community access accurate legal information.
                <br />
                <br />
                📧 Mail us at:{' '}
                <strong>
                  <Link
                    href='mailto:support@gcctaxlaws.com'
                    underline='none'
                    sx={{
                      color: '#5948ca',
                      fontWeight: 600,
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    support@gcctaxlaws.com
                  </Link>
                </strong>
              </Typography>
            )}
            {!isInModal && (
              <Box
                sx={{
                  position: 'fixed',
                  bottom: 20,
                  right: 20,
                  zIndex: 1000,
                }}
              >
                <Zoom in={showBackToTop}>
                  {isMediumScreen ? (
                    <Fab
                      size='small'
                      aria-label='Scroll back to top'
                      onClick={scrollToTop}
                      sx={{
                        backgroundColor: themeColors.primary,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: themeColors.hover,
                        },
                      }}
                    >
                      <KeyboardArrowUpIcon fontSize='small' />
                    </Fab>
                  ) : (
                    <Fab
                      variant='extended'
                      size='medium'
                      aria-label='Scroll back to top'
                      onClick={scrollToTop}
                      sx={{
                        backgroundColor: themeColors.primary,
                        color: 'white',
                        '&:hover': { backgroundColor: themeColors.hover },
                        paddingLeft: 2,
                        paddingRight: 2,
                        borderRadius: buttonBorderRadius,
                      }}
                    >
                      <KeyboardArrowUpIcon sx={{ mr: 1 }} />
                      <Typography
                        variant='button'
                        sx={{
                          fontWeight: 'bold',
                        }}
                      >
                        Back to Top
                      </Typography>
                    </Fab>
                  )}
                </Zoom>
              </Box>
            )}
          </Paper>
          <UpgradePromptModal
            open={showUpgradeModal}
            onClose={closeUpgradeModal}
            reason={upgradeReason}
            quotaResetDate={quotaStatus?.quotaResetDate}
            documentType='link'
          />
        </>
      )
    }
export default Article

